/**
 * Mode detection — the pure geofence that picks the opening app mode AND the
 * device's location context.
 *
 * The two operating modes (`planning` / `shopping`) live in `app-mode.ts`. This
 * module decides two separate things from one device position (T39):
 *
 *   1. `detectMode`  — which operating mode a fresh session should *open* in,
 *      based on where the device is relative to home + selected stores
 *      (the GPS soft-default; a recommendation the orchestrator may apply to the
 *      global `AppMode`). Returns `null` when no confident decision can be made.
 *   2. `detectLocationContext` — a richer, session-only interpretation of WHERE
 *      the device is: unknown / home / in-store / elsewhere, carrying the matched
 *      selected-store id when in store. The Deals surface uses the matched store
 *      id to default its store filter; nothing about it is persisted.
 *
 * Both run off the same geofence math against the same input, so the store-id
 * space is shared: `selectedStores` retains each store's id alongside its coords,
 * and `LocationContext.matchedStoreId` is exactly one of those ids (the value
 * Deals' store filter keys on).
 *
 * Pure per CP3: no I/O, no platform globals, no clock. The web glue
 * (`apps/web/src/stores/useModeDetection.ts`) reads the device position via the
 * Geolocation API + the home/store coords from the store, calls both functions,
 * and applies the results. Native will reuse these with a different position
 * source.
 *
 * Privacy stance (D2): coordinates never leave the device. All distance math
 * runs here, client-side, against coords already loaded for the household and
 * its selected stores.
 */

import type { AppMode } from './app-mode';

// ---------------------------------------------------------------------------
// Tunable constants (D1, D4) — named here so they can be adjusted without
// touching the platform glue.
// ---------------------------------------------------------------------------

/**
 * "At home" radius. Haversine distance from the device to the household home
 * coords must be ≤ this to count as home. 200 m covers GPS error plus the
 * size of a house or apartment building without reaching the next house.
 */
export const HOME_RADIUS_M = 200;

/**
 * "In a store" radius. Distance from the device to the nearest *selected*
 * store must be ≤ this. 200 m covers the building plus its parking lot.
 */
export const STORE_RADIUS_M = 200;

/**
 * Accuracy ceiling. A fix reporting accuracy worse (larger) than this is
 * treated as unavailable — a fuzzy fix must never flip the opening mode (D4).
 */
export const ACCURACY_CEILING_M = 500;

// ---------------------------------------------------------------------------
// Inputs / outputs
// ---------------------------------------------------------------------------

/** A latitude/longitude pair in decimal degrees. */
export interface Coords {
  latitude: number;
  longitude: number;
}

/** Device position as reported by the platform geolocation source. */
export interface DevicePosition extends Coords {
  /** Reported horizontal accuracy in metres (Geolocation API `accuracy`). */
  accuracy: number;
}

/**
 * A selected store as a geofence candidate. Carries the store-location id with
 * its coords so the location context can name the matched store (the id Deals'
 * store filter keys on — a bare coordinate array cannot identify it). T39.
 */
export interface SelectedStore extends Coords {
  /** The store-location id (same id space as the Deals store filter). */
  id: string;
}

export interface DetectModeInput {
  /** Where the device currently is. */
  position: DevicePosition;
  /** Household home coords. `null` when the household has no coords set. */
  home: Coords | null;
  /**
   * The household's *selected* stores (the visibility gate), each with its id +
   * coords. Empty when the household has selected no stores or none have coords.
   */
  selectedStores: SelectedStore[];
  /** Override the home radius (defaults to {@link HOME_RADIUS_M}). */
  homeRadiusM?: number;
  /** Override the store radius (defaults to {@link STORE_RADIUS_M}). */
  storeRadiusM?: number;
  /** Override the accuracy ceiling (defaults to {@link ACCURACY_CEILING_M}). */
  accuracyCeilingM?: number;
}

export type DetectModeReason = 'at-home' | 'in-store';

export interface DetectModeResult {
  mode: AppMode;
  reason: DetectModeReason;
}

/**
 * Where the device is, interpreted relative to home + selected stores (T39).
 * Session-only — never persisted.
 *
 *   - `unknown`  — no confident read (low accuracy, no fix, no reference coords).
 *   - `home`     — within the home radius (and nearer home than any store).
 *   - `in-store` — within a selected store's radius; `matchedStoreId` is set.
 *   - `elsewhere`— a good fix, but not at home and not at any selected store.
 */
export type LocationContextKind = 'unknown' | 'home' | 'in-store' | 'elsewhere';

export interface LocationContext {
  kind: LocationContextKind;
  /** The matched selected-store id, set only when `kind === 'in-store'`. */
  matchedStoreId: string | null;
}

/** The session default before any location result resolves. */
export const UNKNOWN_LOCATION_CONTEXT: LocationContext = {
  kind: 'unknown',
  matchedStoreId: null,
};

// ---------------------------------------------------------------------------
// Distance
// ---------------------------------------------------------------------------

const EARTH_RADIUS_M = 6_371_000;

function toRadians(degrees: number): number {
  return (degrees * Math.PI) / 180;
}

/**
 * Great-circle (Haversine) distance between two coordinate pairs, in metres.
 * Exported so the glue + tests can reuse the exact same math.
 */
export function haversineMeters(a: Coords, b: Coords): number {
  const dLat = toRadians(b.latitude - a.latitude);
  const dLng = toRadians(b.longitude - a.longitude);
  const lat1 = toRadians(a.latitude);
  const lat2 = toRadians(b.latitude);

  const sinDLat = Math.sin(dLat / 2);
  const sinDLng = Math.sin(dLng / 2);
  const h =
    sinDLat * sinDLat + Math.cos(lat1) * Math.cos(lat2) * sinDLng * sinDLng;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(h)));
}

// ---------------------------------------------------------------------------
// Shared geofence evaluation
// ---------------------------------------------------------------------------

interface Evaluation {
  /** True when the fix is good enough to interpret at all (D4). */
  usable: boolean;
  homeDistance: number | null;
  homeInRange: boolean;
  /** Nearest selected store + its distance, or null when none in range. */
  nearestStore: { id: string; distance: number } | null;
}

function evaluate(input: DetectModeInput): Evaluation {
  const {
    position,
    home,
    selectedStores,
    homeRadiusM = HOME_RADIUS_M,
    storeRadiusM = STORE_RADIUS_M,
    accuracyCeilingM = ACCURACY_CEILING_M,
  } = input;

  // D4 — a low-quality fix must not decide anything.
  if (
    !Number.isFinite(position.accuracy) ||
    position.accuracy > accuracyCeilingM
  ) {
    return { usable: false, homeDistance: null, homeInRange: false, nearestStore: null };
  }

  const homeDistance = home ? haversineMeters(position, home) : null;
  const homeInRange = homeDistance !== null && homeDistance <= homeRadiusM;

  // Nearest selected store candidate, retaining its id.
  let nearest: { id: string; distance: number } | null = null;
  for (const store of selectedStores) {
    const d = haversineMeters(position, store);
    if (nearest === null || d < nearest.distance) {
      nearest = { id: store.id, distance: d };
    }
  }
  const nearestStore =
    nearest !== null && nearest.distance <= storeRadiusM ? nearest : null;

  return { usable: true, homeDistance, homeInRange, nearestStore };
}

// ---------------------------------------------------------------------------
// Detection
// ---------------------------------------------------------------------------

/**
 * Decide the opening mode from a device position.
 *
 * Returns `null` when no confident decision can be made — the glue then falls
 * through to the last-persisted manual mode, then the default (D3). Detection is
 * strictly additive: it never returns a broken state.
 *
 * Rules (D1):
 *   - Reject fixes worse than the accuracy ceiling → `null` (D4).
 *   - "At home": device within `homeRadiusM` of `home`.
 *   - "In store": device within `storeRadiusM` of the *nearest selected store*.
 *   - Both within range (a store next to home): nearest wins; exact tie →
 *     `shopping` (the actionable, in-aisle mode).
 *   - Neither within range → `null`.
 */
export function detectMode(input: DetectModeInput): DetectModeResult | null {
  const { usable, homeDistance, homeInRange, nearestStore } = evaluate(input);
  if (!usable) return null;

  if (homeInRange && nearestStore !== null) {
    // Both in range — nearest wins; exact tie → shopping (D1).
    if (nearestStore.distance <= (homeDistance as number)) {
      return { mode: 'shopping', reason: 'in-store' };
    }
    return { mode: 'planning', reason: 'at-home' };
  }

  if (nearestStore !== null) {
    return { mode: 'shopping', reason: 'in-store' };
  }
  if (homeInRange) {
    return { mode: 'planning', reason: 'at-home' };
  }

  return null;
}

/**
 * Interpret WHERE the device is (T39) — the session-only `LocationContext`.
 *
 * Runs the same geofence as {@link detectMode} but always returns a context
 * (never null): an unusable fix or no reference coords → `unknown`; a good fix
 * not at home or any store → `elsewhere`. When in store, `matchedStoreId` names
 * the nearest selected store (the id Deals' store filter keys on). The
 * home/store tie-break mirrors `detectMode` so the two never disagree.
 */
export function detectLocationContext(input: DetectModeInput): LocationContext {
  const { usable, homeDistance, homeInRange, nearestStore } = evaluate(input);
  if (!usable) return { kind: 'unknown', matchedStoreId: null };

  if (homeInRange && nearestStore !== null) {
    if (nearestStore.distance <= (homeDistance as number)) {
      return { kind: 'in-store', matchedStoreId: nearestStore.id };
    }
    return { kind: 'home', matchedStoreId: null };
  }

  if (nearestStore !== null) {
    return { kind: 'in-store', matchedStoreId: nearestStore.id };
  }
  if (homeInRange) {
    return { kind: 'home', matchedStoreId: null };
  }

  // A good fix, but no reference coords matched. With no home AND no stores to
  // compare against there is nothing to interpret → unknown; otherwise the
  // device is simply somewhere else.
  const hasReference = input.home !== null || input.selectedStores.length > 0;
  return { kind: hasReference ? 'elsewhere' : 'unknown', matchedStoreId: null };
}
